import React, { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { HomeOutlined, HelpOutline } from '@material-ui/icons'
import { Link, useLocation } from 'react-router-dom'
import routes from 'routes'
import { hasPermission } from 'utils'
import { serverNow } from 'services/api/PublicPrescription'
import './publicPrescription.scss'

export const basePath = routes.prescription
export const publicMessagesPath = `${basePath}/messages`
export const useClock = (): number => {
  const [now, setNow] = useState(serverNow())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(serverNow()), 1000)
    return (): void => window.clearInterval(timer)
  }, [])
  return now
}

export const errorText = (error: any): string => {
  if (error && error.response) {
    if ([401, 403].includes(error.response.status))
      return 'دسترسی انجام این کار را ندارید یا نشست شما منقضی شده است.'
    if (error.response.status === 404) return 'درخواست مورد نظر پیدا نشد.'
    return error.response.data.message || 'دریافت اطلاعات با خطا روبه‌رو شد.'
  }
  return error instanceof Error && !/network|timeout/i.test(error.message)
    ? error.message
    : 'ارتباط با سرور برقرار نشد؛ دوباره تلاش کنید.'
}

export const Rules: React.FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>راهنما و قوانین جستجوی دارو</DialogTitle>
    <DialogContent>
      <div className="public-prescription rules-copy">
        <p>
          در هر درخواست فقط یک دارو ثبت کنید. نوشتن نام دارو ضروری و ارسال عکس
          اختیاری است.
        </p>
        <p>
          ثبت درخواست و جستجو رایگان است. ابتدا شهر و استان شما بررسی می‌شود و
          تنها با اجازهٔ شما جستجو به سایر استان‌ها گسترش می‌یابد.
        </p>
        <p>
          مهلت جستجو ۴۸ ساعت است. اگر دارو پیدا نشود، هزینه‌ای دریافت نمی‌شود.
        </p>
        <p>
          پس از پیدا شدن دارو، هزینهٔ خدمات و اطلاعات پرداخت در جزئیات درخواست
          نمایش داده می‌شود. برای ارسال رسید ۶۰ دقیقه فرصت دارید. بررسی رسید
          مهلت مشخصی ندارد.
        </p>
        <p>
          پس از تأیید پرداخت، نام، نشانی و تلفن داروخانه‌های معرفی‌شده نمایش
          داده می‌شود. موجودی رزرو نمی‌شود؛ پیش از مراجعه تماس بگیرید.
        </p>
        <p>
          شتاب‌دهی اختیاری است؛ هماهنگی و دریافت هزینهٔ کارشناس اختصاصی تلفنی
          انجام می‌شود.
        </p>
      </div>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} color="primary">
        بستن
      </Button>
    </DialogActions>
  </Dialog>
)

export const Page: React.FC<{ title: string; subtitle?: string }> = ({
  title,
  subtitle,
  children,
}) => {
  const [rules, setRules] = useState(false)
  const location = useLocation()
  const isHomePage = location.pathname === routes.dashboard
  const isMessagesPage = location.pathname === publicMessagesPath
  const isRequestsPage =
    location.pathname.startsWith(basePath) && !isMessagesPage
  return (
    <section className="public-prescription" dir="rtl">
      <nav className="public-nav" aria-label="پنل مردمی">
        <Button
          component={Link}
          to="/dashboard"
          className={isHomePage ? 'is-active' : undefined}
          startIcon={<HomeOutlined />}
        >
          خانه
        </Button>
        {hasPermission('prescription:getprescriptionofuser') && (
          <Button
            component={Link}
            to={`${basePath}/my`}
            className={isRequestsPage ? 'is-active' : undefined}
          >
            درخواست‌های من
          </Button>
        )}
        <Button
          component={Link}
          to={publicMessagesPath}
          className={isMessagesPage ? 'is-active' : undefined}
        >
          پیام‌های من
        </Button>
        <Button
          onClick={(): void => setRules(true)}
          startIcon={<HelpOutline />}
        >
          راهنما و قوانین
        </Button>
      </nav>
      <header className="page-heading">
        <Typography variant="h5" component="h1">
          {title}
        </Typography>
        {subtitle && <Typography color="textSecondary">{subtitle}</Typography>}
      </header>
      {children}
      <Rules open={rules} onClose={(): void => setRules(false)} />
    </section>
  )
}

export const LoadState: React.FC<{
  loading: boolean
  error?: unknown
  retry: () => void
}> = ({ loading, error, retry }) => (
  <>
    {loading && (
      <Paper className="request-card" role="status">
        در حال دریافت اطلاعات…
      </Paper>
    )}
    {!!error && (
      <Alert
        severity="error"
        action={<Button onClick={retry}>تلاش دوباره</Button>}
      >
        {errorText(error)}
      </Alert>
    )}
  </>
)

export const Confirm: React.FC<{
  open: boolean
  title: string
  body: string
  busy?: boolean
  onClose: () => void
  onConfirm: () => void
}> = (props) => (
  <Dialog
    open={props.open}
    onClose={props.busy ? undefined : props.onClose}
    maxWidth="xs"
    fullWidth
  >
    <DialogTitle>{props.title}</DialogTitle>
    <DialogContent>
      <Typography>{props.body}</Typography>
    </DialogContent>
    <DialogActions>
      <Button disabled={props.busy} onClick={props.onClose}>
        بازگشت
      </Button>
      <Button disabled={props.busy} onClick={props.onConfirm} color="secondary">
        {props.busy ? 'در حال انجام…' : 'بله، تأیید می‌کنم'}
      </Button>
    </DialogActions>
  </Dialog>
)
